import {
  CogIcon,
  CollectionIcon,
  DocumentDownloadIcon,
  LightBulbIcon,
  LogoutIcon,
  QuestionMarkCircleIcon,
  SearchIcon,
  ShoppingCartIcon,
  TagIcon,
  ChartPieIcon,
  UserCircleIcon,
  FingerPrintIcon,
  LockClosedIcon,
  CreditCardIcon,
  BellIcon,
  KeyIcon,
  ScaleIcon,
  TranslateIcon,
  ChevronRightIcon,
  TerminalIcon,
} from '@heroicons/react/outline'
import { Role } from 'lib/dto'
import { Icon } from 'components/icons'
import { HostType } from './theme'
import { MallCode, mallConfig } from './malls'
import { Context } from 'lib/api'
import {
  BadgePercentIcon,
  BotIcon,
  CalculatorIcon,
  CalendarIcon,
  ClapperboardIcon,
  ChartLineIcon,
  CircleCheckIcon,
  DiamondPercentIcon,
  FileIcon,
  GitCompareIcon,
  HashIcon,
  ImageIcon,
  ListOrderedIcon,
  MapPinnedIcon,
  ImagesIcon,
  MegaphoneIcon,
  MessageCircleQuestionMarkIcon,
  MessageSquareReplyIcon,
  ScrollTextIcon,
  SearchCheckIcon,
  ShapesIcon,
  TextCursorIcon,
  UserSearchIcon,
  UserStarIcon,
  WrenchIcon,
  TagsIcon,
  ScanSearch,
  PackageSearchIcon,
  ChartNoAxesCombinedIcon,
  WarehouseIcon,
  ShelvingUnitIcon,
} from 'lucide-react'
import { c } from 'lib/component-utils'
import LanguagePicker from 'components/sidebar/LanguagePicker'
import MallPicker from 'components/sidebar/MallPicker'
import ThemeSelect from 'components/settings/ThemeSelect'
import { getDefaultStore } from 'jotai'
import { documentsAtom } from 'components/app/DocumentNotifier'

type ConfigSubnavItem = {
  name: string
  translationKey?: string
  icon?: Icon
  active?: string[]
  route: string
  addMall?: boolean
  role?: Role
  malls?: MallCode[]
  hosts?: HostType[]
  customVisibilityCondition?: (ctx: Context) => boolean
  customRender?: (
    ctx: Context,
    t: (key: string | TemplateStringsArray, options?: object) => string,
    item: SubnavItem
  ) => React.ReactNode
}

export type SubnavItem = ConfigSubnavItem & {
  active: string[]
  role: Role
  addMall: boolean
}

type ConfigNavItem<T = ConfigSubnavItem> = T & {
  icon: Icon
  customIcon?: (
    ctx: Context,
    t: (key: string | TemplateStringsArray) => string,
    item: ConfigNavItem,
    active: boolean
  ) => React.ReactNode
  customName?: (
    ctx: Context,
    t: (key: string | TemplateStringsArray) => string,
    item: ConfigNavItem,
    active: boolean
  ) => React.ReactNode
  items: T[]
  role: Role
}

export type NavItem = ConfigNavItem<SubnavItem>

const withDefaults = (navbar: ConfigNavItem[]): NavItem[] => {
  for (const item of navbar) {
    item.active ??= []
    item.active.push(item.route)
    item.addMall ??= false
    for (const subitem of item.items) {
      subitem.role ??= item.role
      subitem.hosts ??= item.hosts
      subitem.malls ??= item.malls
      subitem.active ??= []
      item.active.push(subitem.route)
      subitem.active.push(subitem.route)
      subitem.addMall ??= item.addMall
    }
  }

  return navbar as NavItem[]
}

const navbarConfig: ConfigNavItem[] = [
  {
    name: 'change_mall',
    icon: () => '',
    customIcon: (ctx, t, item, active) => (
      <img
        className="px-[11px] pt-[11px] pb-[3px] large-sidebar:p-0 large-sidebar:w-6 large-sidebar:h-6 shrink-0 large-sidebar:-ml-1 large-sidebar:mr-3"
        src={`/img/mall_ico/${ctx.currentMall?.code}.png`}
        alt={`${ctx.currentMall?.description} logo`}
      />
    ),
    customName: (ctx, t, item, active) => (
      <div className="flex items-center gap-2">
        <div className="flex flex-col text-left">
          <span className="hidden font-medium text-gray-600 large-sidebar:inline group-hover:text-gray-900">
            {ctx.currentMall.description}
          </span>
          <span className="whitespace-nowrap group-hover:text-gray-600 large-sidebar:inline hidden text-[11px] text-gray-500">{t`change_mall`}</span>
        </div>
        <ChevronRightIcon className="absolute hidden w-4 h-4 text-gray-500 right-2 large-sidebar:block shrink-0" />
      </div>
    ),
    role: 'ROLE_USER',
    route: '',
    addMall: true,
    items: [
      {
        name: 'malls',
        route: '',
        customRender: (ctx, t, item) => <MallPicker />,
      },
      { name: 'placeholder', route: '', customRender: () => <div></div> },
    ],
  },
  {
    name: 'pages.business.index',
    icon: ChartPieIcon,
    role: 'ROLE_USER',
    malls: ['wb', 'ozon', 'ozon2', 'beru', 'uzum'],
    route: '/business/sales/',
    addMall: true,
    items: [
      {
        name: 'sales',
        icon: ChartPieIcon,
        route: '/business/sales/',
        malls: ['wb', 'ozon', 'ozon2', 'beru', 'uzum'],
      },
      {
        name: 'orders',
        icon: ChartLineIcon,
        route: '/business/orders/',
        malls: ['wb', 'ozon', 'ozon2', 'beru', 'uzum'],
      },
      {
        name: 'geography',
        icon: MapPinnedIcon,
        route: '/business/geography/',
        malls: ['wb', 'ozon', 'beru'],
      },
      {
        name: 'stocks',
        translationKey: 'pages.business.stocks',
        icon: ShelvingUnitIcon,
        route: '/business/stocks/',
        malls: ['wb', 'ozon', 'ozon2', 'beru', 'uzum'],
      },
    ],
  },
  {
    name: 'pages.business.tools',
    icon: WrenchIcon,
    role: 'ROLE_USER',
    route: '/business/bidder/',
    addMall: true,
    items: [
      {
        name: 'bidder',
        icon: MegaphoneIcon,
        route: '/business/bidder/',
        malls: ['wb'],
      },
      {
        name: 'repricer',
        icon: BadgePercentIcon,
        route: '/business/repricer/',
        malls: ['wb', 'ozon', 'ozon2'],
      },
      {
        name: 'pages.business.reviews',
        icon: MessageSquareReplyIcon,
        route: '/business/reviews/',
        malls: ['wb', 'ozon', 'ozon2', 'beru'],
      },
      {
        name: 'supply_planning',
        icon: CalendarIcon,
        route: '/business/supply-planning/',
        malls: ['wb', 'ozon', 'ozon2', 'beru'],
      },
      {
        name: 'unit-economics',
        icon: CalculatorIcon,
        route: '/business/unit-economics/',
        malls: ['wb', 'ozon', 'ozon2'],
      },
    ],
  },
  {
    name: 'pages.products',
    icon: ShoppingCartIcon,
    role: 'ROLE_USER',
    active: ['/products/info'],
    route: '/products/list/',
    addMall: true,
    items: [
      {
        name: 'product_listing',
        icon: ShoppingCartIcon,
        route: '/products/list/',
      },
      {
        name: 'product_comparison',
        icon: GitCompareIcon,
        route: '/products/comparison/',
      },
      {
        name: 'positions_in_recommendations.nav',
        icon: ListOrderedIcon,
        route: '/products/positions/',
        malls: ['wb'],
      },
      {
        name: 'monitoring.nav',
        icon: ScanSearch,
        route: '/products/query-monitoring/',
        customVisibilityCondition: (ctx) =>
          !mallConfig[ctx.currentMall.code]?.excludeBlocks?.has('placement'),
      },
    ],
  },
  {
    name: 'pages.categories',
    icon: CollectionIcon,
    role: 'ROLE_USER',
    malls: [
      'wb',
      'noon',
      'noon_saudi',
      'nana',
      'amazon_ae',
      'citilink',
      'lamoda',
      'dns',
      'zdravcity',
      'lavka',
      'vprok',
      'decathlon',
      'talabat',
      'goods',
      'mvideo',
      'goldapple',
      'samokat',
      'magnit_cosmetic',
      'magnit_market',
      'ozon',
      'ozon2',
      'ozon_fresh',
      'uzum',
    ],
    route: '/categories/list/',
    addMall: true,
    items: [
      {
        name: 'categories',
        icon: CollectionIcon,
        route: '/categories/list/',
      },
      {
        name: 'subjects',
        icon: ShapesIcon,
        route: '/subjects/list/',
        malls: ['wb'],
      },
      {
        name: 'merchants',
        icon: ScaleIcon,
        route: '/merchants/list/',
        malls: [
          'wb',
          'noon',
          'noon_saudi',
          'nana',
          'amazon_ae',
          'lamoda',
          'lavka',
          'talabat',
          'goods',
          'mvideo',
          'samokat',
          'ozon',
          'ozon2',
          'ozon_fresh',
          'uzum',
        ],
      },
      {
        name: 'brands',
        icon: TagIcon,
        route: '/brands/list/',
        malls: [
          'wb',
          'noon',
          'noon_saudi',
          'nana',
          'amazon_ae',
          'citilink',
          'lamoda',
          'dns',
          'zdravcity',
          'lavka',
          'vprok',
          'decathlon',
          'goods',
          'mvideo',
          'goldapple',
          'samokat',
          'ozon',
          'ozon2',
          'ozon_fresh',
          'uzum',
        ],
      },
      {
        name: 'niche_analysis',
        icon: PackageSearchIcon,
        route: '/niches/list/',
        malls: ['wb'],
      },
    ],
  },
  {
    name: 'brand_analytics',
    icon: TagIcon,
    role: 'ROLE_USER',
    route: '/brands/content/',
    addMall: true,
    items: [
      {
        name: 'content',
        icon: ImagesIcon,
        route: '/brands/content/',
        role: 'ROLE_BRAND',
      },
      {
        name: 'pricing',
        icon: DiamondPercentIcon,
        route: '/brands/pricing/',
        role: 'ROLE_BRAND',
      },
      {
        name: 'placement',
        icon: SearchCheckIcon,
        route: '/brands/placement/',
        role: 'ROLE_BRAND',
      },
      {
        name: 'reviews',
        icon: UserStarIcon,
        route: '/brands/reviews/',
        role: 'ROLE_BRAND',
      },
      {
        name: 'questions',
        icon: MessageCircleQuestionMarkIcon,
        route: '/brands/questions/',
        role: 'ROLE_BRAND',
        malls: ['beru', 'citilink', 'dns', 'lamoda', 'ozon', 'ozon2', 'shopify', 'wb', 'goods'],
      },
      {
        name: 'availability',
        icon: CircleCheckIcon,
        route: '/brands/availability/',
        role: 'ROLE_BRAND',
      },
    ],
  },
  {
    name: 'search_queries',
    icon: SearchIcon,
    role: 'ROLE_USER',
    route: '/keywords/list/',
    addMall: true,
    items: [
      {
        name: 'search_queries',
        icon: SearchIcon,
        route: '/keywords/list/',
        active: ['/keywords/info'],
        malls: ['wb', 'ozon', 'ozon2'],
      },
      {
        name: 'demand_analytics',
        icon: ChartNoAxesCombinedIcon,
        route: '/keywords/demand-analytics/',
        malls: ['wb'],
      },
      {
        name: 'pages.keywords',
        icon: UserSearchIcon,
        route: '/keywords/my-keywords/',
        addMall: false,
        customVisibilityCondition: (ctx) => {
          return (
            (ctx.user.roles.includes('ROLE_BRAND') ||
              (ctx.user.roles.includes('ROLE_USER') && ['wb'].includes(ctx.currentMall.code))) &&
            !['psb', 'vtb'].includes(ctx.theme.hostType)
          )
        },
      },
    ],
  },
  {
    name: 'pages.ai_tools',
    icon: BotIcon,
    role: 'ROLE_USER',
    route: '/tools/description-generation/',
    addMall: true,
    items: [
      {
        name: 'pages.ai_generation',
        icon: TextCursorIcon,
        route: '/tools/description-generation/',
      },
      {
        name: 'pages.image_generation',
        icon: ImageIcon,
        route: '/tools/image-generation/',
        addMall: false,
        hosts: ['sellmonitor', 'sellematics', 'psb', 'vtb', 'mayak'],
      },
      {
        name: 'pages.video_generation',
        icon: ClapperboardIcon,
        route: '/tools/video-generation/',
        addMall: false,
        hosts: ['sellmonitor', 'sellematics', 'psb', 'vtb', 'mayak'],
      },
      {
        name: 'pages.seo',
        icon: HashIcon,
        route: '/tools/seo-optimization/',
        malls: ['wb', 'ozon', 'ozon2'],
      },
    ],
  },
]

const _additionalRoutes: ConfigNavItem[] = [
  {
    name: 'pages.exports',
    icon: DocumentDownloadIcon,
    role: 'ROLE_USER',
    route: '/export/list/',
    items: [
      {
        name: 'exports',
        route: '/export/list/',
      },
    ],
  },
  {
    name: 'help',
    icon: QuestionMarkCircleIcon,
    role: 'ROLE_USER',
    route: '/help/inner/sales/',
    active: ['/help/'],
    hosts: ['sellmonitor', 'sellematics', 'uzum', 'psb', 'vtb', 'mayak'],
    items: [
      {
        name: 'instructions',
        icon: QuestionMarkCircleIcon,
        route: '/help/inner/sales/',
      },
      {
        name: 'changelog',
        icon: ScrollTextIcon,
        route: '/help/changelog/',
        hosts: ['sellmonitor', 'sellematics'],
      },
      {
        name: 'suggestions.title',
        icon: LightBulbIcon,
        route: '/help/suggestions/',
        hosts: ['sellmonitor'],
        customVisibilityCondition: (ctx) => {
          return (
            ['sellmonitor'].includes(ctx.theme.hostType) &&
            (ctx.user.hasPreviousPayments || ctx.user.roles.includes('ROLE_ADMIN'))
          )
        },
      },
    ],
  },
  {
    name: 'language',
    icon: TranslateIcon,
    route: '',
    role: 'ROLE_USER',
    items: [
      {
        name: 'language_picker',
        route: '',
        customRender: (ctx, t, item) => <LanguagePicker />,
      },
      { name: 'placeholder', route: '', customRender: () => <div></div> },
    ],
  },
  {
    name: 'settings',
    icon: CogIcon,
    customIcon: (ctx, t, item, active) => (
      <div className="large-sidebar:-ml-2 large-sidebar:mr-2.5">
        <div
          className={c`size-[30px] p-1 rounded-md shrink-0 grow-0 flex items-center justify-center ${active} bg-primary-800 group-hover:bg-primary-700 | bg-gray-500 group-hover:bg-gray-600`}
        >
          <CogIcon className="text-white" />
        </div>
      </div>
    ),
    role: 'ROLE_USER',
    route: '/settings/webauthn/',
    active: ['/settings/'],
    items: [
      {
        name: 'user_info',
        route: '',
        customRender: (ctx, t, item) => (
          <div className="group px-2 py-0.5 font-medium text-gray-700">
            <div className="py-2.5 px-4 rounded-t-lg flex items-center gap-2 bg-gray-100">
              <UserCircleIcon className="w-6 h-6 -ml-[3px]" />
              <div className="text-[15px]">{ctx.user.phone}</div>
            </div>
          </div>
        ),
      },
      {
        name: 'logout',
        route: '/logout/',
        customRender: (ctx, t, item) => {
          return (
            <a
              href={ctx.user.switched ? '/admin/?_switch_user=_exit' : '/logout/'}
              className="-mt-2 group px-2 py-0.5 font-medium text-gray-700"
            >
              <div className="relative py-2.5 px-4 rounded-b-lg flex items-center gap-2 bg-gray-100 group-hover:bg-gray-200">
                <div className="absolute top-0 border-t inset-x-3 border-black/5" />
                <LogoutIcon className="w-5 h-5 -ml-px" />
                <div className="">{ctx.user.switched ? t`logout_switch_back` : t`logout`}</div>
              </div>
            </a>
          )
        },
      },
      {
        name: 'pages.settings.webauthn',
        icon: FingerPrintIcon,
        route: '/settings/webauthn/',
        hosts: ['sellmonitor', 'mayak'],
      },
      {
        name: 'password',
        icon: LockClosedIcon,
        route: '/settings/password/',
        hosts: ['sellmonitor', 'sellematics', 'sellscreen', 'uzum'],
        customVisibilityCondition: (ctx) =>
          ['sellmonitor', 'sellematics', 'sellscreen', 'uzum'].includes(ctx.theme.hostType) &&
          (!ctx.user.twoFactorEnabled || ctx.user.roles.includes('ROLE_ADMIN')),
      },
      { name: 'brands', icon: TagsIcon, route: '/settings/brands/', hosts: ['sellematics'] },
      {
        name: 'subscription',
        icon: CreditCardIcon,
        route: '/settings/subscription/',
        hosts: ['sellmonitor', 'sellematics', 'sellscreen', 'uzum', 'mayak'],
      },
      {
        name: 'notifications',
        icon: BellIcon,
        route: '/settings/notifications/',
        hosts: ['sellmonitor', 'sellematics', 'psb', 'vtb', 'mayak'],
      },
      {
        name: 'documents.documents',
        icon: FileIcon,
        route: '/settings/documents/',
        hosts: ['sellmonitor'],
        customVisibilityCondition: (ctx) => {
          const store = getDefaultStore()
          const documents = store.get(documentsAtom)
          return (
            ['sellmonitor'].includes(ctx.theme.hostType) &&
            documents !== null &&
            documents.items.length > 0
          )
        },
      },
      {
        name: 'accesses',
        icon: KeyIcon,
        route: '/business/accesses/',
        malls: ['wb', 'ozon', 'ozon2', 'beru', 'uzum'],
        addMall: true,
      },
      {
        name: 'pages.settings.api_integrations',
        icon: TerminalIcon,
        route: '/settings/api-integrations/',
        hosts: ['sellmonitor', 'sellematics'],
      },
      {
        name: 'theme',
        route: '',
        customRender: (ctx, t, item) => (
          <div className="px-[18px] py-3 font-medium text-gray-700">
            <div className="flex flex-col gap-2 rounded-lg">
              <ThemeSelect />
            </div>
          </div>
        ),
      },
    ],
  },
]

export const navbar = withDefaults(navbarConfig)
export const additionalRoutes = withDefaults(_additionalRoutes)
